import type { ReactNode } from "react";
import { useMemo } from "react";
import type { TypographyVariants } from "@/components/shared/typography";
import { Typography } from "@/components/shared/typography";
import { View } from "@/components/ui/view";
import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

const badgeVariants = cva("native:flex inline-flex items-center gap-1 transition-colors", {
  variants: {
    variant: {
      primary: "border-brand-primary-border bg-brand-primary-surface",
      success: "border-success-border bg-success-surface",
      danger: "border-danger-border bg-danger-surface",
      "danger-fill": "border-danger bg-danger",
      info: "border-info-border bg-info-surface",
      warning: "border-warning-border bg-warning-surface",
      neutral: "border-border-high bg-background-intermediate",
      "over-static": "bg-brand-over-static border-transparent",
    },
    size: {
      sm: "px-2 py-1",
      md: "px-2 py-1.5",
      lg: "px-3 py-1.5",
    },
    outline: {
      true: "border",
      false: "border-0",
    },
    shape: {
      pill: "rounded-full",
      chip: "rounded-md",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "md",
    outline: true,
    shape: "pill",
  },
});
export type BadgeProps = VariantProps<typeof badgeVariants>;

export type BadgeVariant = NonNullable<BadgeProps["variant"]>;

export function Badge({
  className,
  variant,
  size,
  outline,
  shape,
  children,
  ...props
}: BadgeProps & {
  className?: string;
  children: ReactNode;
}) {
  const textColor: keyof TypographyVariants["color"] = useMemo(() => {
    switch (variant) {
      case "primary":
        return "brand-primary";
      case "danger":
        return "danger";
      case "success":
        return "success-on-surface";
      case "info":
        return "info";
      case "warning":
        return "warning-on-surface";
      case "danger-fill":
      case "over-static":
        return "white";
      default:
        return "foreground-intermediate";
    }
  }, [variant]);

  const textSize: keyof TypographyVariants["variant"] = useMemo(() => {
    switch (size) {
      case "sm":
        return "caption-md";
      case "md":
        return "body-sm";
      case "lg":
        return "label-sm";
      default:
        return "body-sm";
    }
  }, [size]);

  return (
    <View className={cn(badgeVariants({ variant, size, outline, shape }), className)} {...props}>
      {["string", "number"].includes(typeof children) ? (
        <Typography as="span" color={textColor} variant={textSize} numberOfLines={1}>
          {children}
        </Typography>
      ) : (
        children
      )}
    </View>
  );
}
